// views/Dashboard/Bonuses/components/CountdownTimer/CountdownTimer.tsx
import { useState, useEffect } from 'react';
import styles from './CountdownTimer.module.css';

type Phase = 'waiting' | 'active' | 'expired';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountdownTimerProps {
  endDate: number;
  startDate?: number | null;
  variant?: 'expiry' | 'upcoming';
  size?: 'normal' | 'small';
}

const CountdownTimer = ({ endDate, startDate = null, size = 'normal' }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState<Phase>('waiting');

  useEffect(() => {
    const split = (difference: number): TimeLeft => ({
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
    });

    const calculateTimeLeft = () => {
      const now = Date.now();
      const start = startDate ? new Date(startDate).getTime() : now;
      const end = new Date(endDate).getTime();

      if (startDate && now < start) {
        setPhase('waiting');
        setTimeLeft(split(start - now));
        setIsActive(true);
      } else if (now >= start && now <= end) {
        const difference = end - now;
        if (difference > 0) {
          setPhase('active');
          setTimeLeft(split(difference));
          setIsActive(true);
        } else {
          setPhase('expired');
          setIsActive(false);
        }
      } else {
        setPhase('expired');
        setIsActive(false);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [endDate, startDate]);

  const getDisplayText = () => {
    if (phase === 'expired') return 'Vypršel';
    if (phase === 'waiting') return 'Začne za:';
    return 'Zbývá:';
  };

  const getTimerClass = () => {
    const baseClass = `${styles.countdownTimer} ${styles[size]}`;
    if (phase === 'expired') return `${baseClass} ${styles.expired}`;
    if (phase === 'waiting') return `${baseClass} ${styles.upcoming}`;

    const totalHours = timeLeft.days * 24 + timeLeft.hours;
    if (totalHours <= 1) return `${baseClass} ${styles.critical}`;
    if (totalHours <= 24) return `${baseClass} ${styles.warning}`;
    return `${baseClass} ${styles.normal}`;
  };

  const formatTime = () => {
    if (phase === 'expired') return '00:00:00';
    const pad = (n: number) => String(n).padStart(2, '0');
    const hms = `${pad(timeLeft.hours)}:${pad(timeLeft.minutes)}:${pad(timeLeft.seconds)}`;
    return timeLeft.days > 0 ? `${timeLeft.days}d ${hms}` : hms;
  };

  return (
    <div className={getTimerClass()}>
      <div className={styles.timerContent}>
        <span className={styles.timerLabel}>{getDisplayText()}</span>
        <span className={styles.timerValue}>{formatTime()}</span>
      </div>
      {isActive && <div className={styles.pulseAnimation} />}
    </div>
  );
};

export default CountdownTimer;
